import React, { useCallback, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import DataGrid from "@dtci/spellbook/DataGrid";
import styles from "../../../../components/AGGrid/details-table.module.scss";
import { JobStatusModal } from "../modals/status";
import { JobTasksModal } from "../modals/tasks";
import { useAgGridApi } from "tap/components/AGGrid/hooks.js";
import { useJobsGridUtils } from "./utils/gridUtils";
import { useJobsActions } from "tap/store/tapJobs/useJobs.js";
import { useJobsData } from "../../hooks/useJobsData";
import { mapJobTypeToLabel } from "tap/utils/jobType.js";

/**
 * TAP Jobs Grid - Displays TAP jobs with modals for status and tasks.
 */
export const JobsGrid = () => {
  const agGridApi = useAgGridApi();
  const { jobs, jobTypeOptions, refreshJobs } = useSelector((s) => s.tapJobs);
  const { fetchJobs, tapJobRange, filterForm } = useJobsData();

  /** Maps job type codes to human-readable labels when jobTypeOptions loads */
  const mappedJobs = useMemo(() => {
    return mapJobTypeToLabel(jobs, jobTypeOptions);
  }, [jobs, jobTypeOptions]);
  const { updateJobs, updateJobsSearchForm } = useJobsActions();

  const refreshJobsList = async () => {
    const data = await fetchJobs(tapJobRange, filterForm, null, true);
    if (data) {
      updateJobs({
        jobs: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber,
      });
    }
  };

  useEffect(() => {
    if (refreshJobs) {
      refreshJobsList();
      updateJobs({ refreshJobs: false });
    }
  }, [refreshJobs]);

  const onOpenJobViewModal = useCallback(
    (job) => {
      updateJobs({
        selectedJob: job,
        isJobViewModalOpen: true,
      });
    },
    [updateJobs],
  );

  const onOpenJobTasksModal = useCallback(
    (job) => {
      if (!job || !job.id) return;

      updateJobs({
        selectedJob: job,
        isTasksModalOpen: true,
      });

      updateJobsSearchForm({
        key: "taskFilterForm",
        data: { jobId: job.id },
      });
    },
    [updateJobs, updateJobsSearchForm],
  );

  const { columns } = useJobsGridUtils({
    columnHandlers: [onOpenJobViewModal, onOpenJobTasksModal],
  });

  return (
    <>
      <DataGrid
        className={styles.dataGrid}
        autoSizing
        agGrid={{
          ref: agGridApi.AgGridApiRef,
          rowData: mappedJobs ?? [],
          suppressColumnStateReset: true,
          headerHeight: 45,
          defaultColDef: {
            wrapText: true,
            autoHeight: true,
            resizable: true,
            suppressHeaderMenuButton: true,
            cellStyle: {
              paddingTop: "10px",
              paddingBottom: "10px",
              wordBreak: "break-word",
            },
          },
          getRowId: (params) => String(params.data.id),
          suppressScrollOnNewData: true,
        }}
        bordered="none"
        columnDefs={columns}
        sortable={false}
        striped
        noResultsMessage={"No Jobs Found"}
      />
      <JobStatusModal />
      <JobTasksModal />
    </>
  );
};
